import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { AxiosResponse } from "axios";
import { RootState } from "../store";
import { axiosApi, getResponseError, handleErrorRTKAndAxiosAsync } from "@/helpers";
import { ENDPOINTS_QAS, URL_QAS } from "@/constants/api_endpoints";
import { ICopyAnswersProps, IQasCopyAnswersWithCleaningState, IResponseCopyAnswersStatus, IResponseLogCopyAnswers } from "@/types/qas/qasCopyAnswersWithCleaningTypes";
import { IResponse, RequestStatus, ResponseStatus } from "@/types/statusTypes";

const initialState: IQasCopyAnswersWithCleaningState = {
	copyingAnswersStart: {
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
	},
	copyingAnswersStop: {
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
	},
	copyingAnswersStatus: {
		requestStatus: RequestStatus.IDLE,
		error: "",
		responseStatus: "stopped",
		progress: 0,
	},
	copyingAnswersLog: {
		status: RequestStatus.IDLE,
		data: [],
		error: ResponseStatus.SUCCESS,
		message: "",
	},
};

// старт копирования ответов
export const copyAnswersWithCleaning = createAsyncThunk(
	"qasCopyAnswers/start",
	async ({ categoryId, channelFrom, channelTo, promptId }: ICopyAnswersProps, { rejectWithValue }) => {
		try {
			const response: AxiosResponse<IResponse> = await axiosApi.post(`${URL_QAS}/${ENDPOINTS_QAS.COPY_ANSWERS_WITH_CLEANING_START}`, {
				category: categoryId,
				channelfrom: channelFrom,
				channelto: channelTo,
				prompt: promptId,
			});
			return response.data;
		} catch (error) {
			const handledError = await handleErrorRTKAndAxiosAsync(error);
			return rejectWithValue(handledError);
		}
	}
);

// остановка копирования ответов
export const stopCopyingAnswers = createAsyncThunk("qasCopyAnswers/stop", async (_, { rejectWithValue }) => {
	try {
		const response: AxiosResponse<IResponse> = await axiosApi.get(`${URL_QAS}/${ENDPOINTS_QAS.COPY_ANSWERS_WITH_CLEANING_STOP}`);
		return response.data;
	} catch (error) {
		const handledError = await handleErrorRTKAndAxiosAsync(error);
		return rejectWithValue(handledError);
	}
});

// статус копирования ответов
export const getStatusCopyingAnswers = createAsyncThunk(
	"qasCopyAnswers/status",
	async (_, { rejectWithValue }) => {
		try {
			const response: AxiosResponse<IResponseCopyAnswersStatus> = await axiosApi.get(`${URL_QAS}/${ENDPOINTS_QAS.COPY_ANSWERS_WITH_CLEANING_STATUS}`);
			return response.data;
		} catch (error) {
			const handledError = await handleErrorRTKAndAxiosAsync(error);
			return rejectWithValue(handledError);
		}
	},
	{
		condition: (_, { getState }) => {
			if ((getState() as RootState).qasProcessingQuestions.processingQuestionsStatus.requestStatus === RequestStatus.LOADING) return false;
		},
	}
);

// логи копирования ответов
export const getLogCopyingAnswers = createAsyncThunk("qasCopyAnswers/log", async (_, { rejectWithValue }) => {
	try {
		const response: AxiosResponse<IResponseLogCopyAnswers | IResponse> = await axiosApi.get(`${URL_QAS}/${ENDPOINTS_QAS.COPY_ANSWERS_WITH_CLEANING_LOG}`);
		return response.data;
	} catch (error) {
		const handledError = await handleErrorRTKAndAxiosAsync(error);
		return rejectWithValue(handledError);
	}
});

const qasCopyAnswersWithCleaningSlice = createSlice({
	name: "qasCopyAnswers",
	initialState,
	reducers: {
		// очистка state
		clearState: (state) => {
			state.copyingAnswersStart = initialState.copyingAnswersStart;
			state.copyingAnswersStop = initialState.copyingAnswersStop;
			state.copyingAnswersStatus = initialState.copyingAnswersStatus;
			state.copyingAnswersLog = initialState.copyingAnswersLog;
		},
		// очистка статуса старта копирования ответов
		clearCopyingAnswersStart: (state) => {
			state.copyingAnswersStart = initialState.copyingAnswersStart;
		},
		// очистка статуса остановки копирования ответов
		clearCopyingAnswersStop: (state) => {
			state.copyingAnswersStop = initialState.copyingAnswersStop;
		},
		// очистка статуса копирования ответов
		clearCopyingAnswersStatus: (state) => {
			state.copyingAnswersStatus = initialState.copyingAnswersStatus;
		},
		// очистка логов копирования ответов
		clearCopyingAnswersLog: (state) => {
			state.copyingAnswersLog = initialState.copyingAnswersLog;
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(copyAnswersWithCleaning.pending, (state) => {
				state.copyingAnswersStart.status = RequestStatus.LOADING;
			})
			.addCase(copyAnswersWithCleaning.fulfilled, (state, action) => {
				if (action.payload) {
					state.copyingAnswersStart.status = RequestStatus.IDLE;
					state.copyingAnswersStart.error = action.payload.error;
					state.copyingAnswersStart.message = action.payload.message;
				} else state.copyingAnswersStart.status = RequestStatus.FAILED;
			})
			.addCase(copyAnswersWithCleaning.rejected, (state, action: PayloadAction<unknown>) => {
				state.copyingAnswersStart.status = RequestStatus.FAILED;
				const { error, message } = getResponseError(action);
				state.copyingAnswersStart.error = error;
				state.copyingAnswersStart.message = message;
			})
			.addCase(stopCopyingAnswers.pending, (state) => {
				state.copyingAnswersStop.status = RequestStatus.LOADING;
			})
			.addCase(stopCopyingAnswers.fulfilled, (state, action) => {
				if (action.payload) {
					state.copyingAnswersStop.status = RequestStatus.IDLE;
					state.copyingAnswersStop.error = action.payload.error;
					state.copyingAnswersStop.message = action.payload.message;
				} else state.copyingAnswersStop.status = RequestStatus.FAILED;
			})
			.addCase(stopCopyingAnswers.rejected, (state, action: PayloadAction<unknown>) => {
				state.copyingAnswersStop.status = RequestStatus.FAILED;
				const { error, message } = getResponseError(action);
				state.copyingAnswersStop.error = error;
				state.copyingAnswersStop.message = message;
			})
			.addCase(getStatusCopyingAnswers.pending, (state) => {
				state.copyingAnswersStatus.requestStatus = RequestStatus.LOADING;
			})
			.addCase(getStatusCopyingAnswers.fulfilled, (state, action) => {
				if (action.payload && typeof action.payload !== "string") {
					state.copyingAnswersStatus.requestStatus = RequestStatus.IDLE;
					state.copyingAnswersStatus.error = action.payload.error;
					state.copyingAnswersStatus.responseStatus = action.payload.status;
					state.copyingAnswersStatus.progress = action.payload.progress;
				} else state.copyingAnswersStatus.requestStatus = RequestStatus.FAILED;
			})
			.addCase(getStatusCopyingAnswers.rejected, (state, action: PayloadAction<unknown>) => {
				state.copyingAnswersStatus.requestStatus = RequestStatus.FAILED;
				const { error } = getResponseError(action);
				state.copyingAnswersStatus.error = error.toString();
			})
			.addCase(getLogCopyingAnswers.pending, (state) => {
				state.copyingAnswersLog.status = RequestStatus.LOADING;
			})
			.addCase(getLogCopyingAnswers.fulfilled, (state, action) => {
				if (action.payload && typeof action.payload === "object" && "answers" in action.payload && Array.isArray(action.payload.answers)) {
					state.copyingAnswersLog.status = RequestStatus.IDLE;
					state.copyingAnswersLog.data = action.payload.answers;
				} else state.copyingAnswersLog.status = RequestStatus.FAILED;
			})
			.addCase(getLogCopyingAnswers.rejected, (state, action: PayloadAction<unknown>) => {
				state.copyingAnswersLog.status = RequestStatus.FAILED;
				const { error, message } = getResponseError(action);
				state.copyingAnswersLog.error = error;
				state.copyingAnswersLog.message = message;
			});
	},
});

export const { clearState, clearCopyingAnswersStart, clearCopyingAnswersStop, clearCopyingAnswersStatus, clearCopyingAnswersLog } =
	qasCopyAnswersWithCleaningSlice.actions;

export const selectCopyingAnswersStartStatus = (state: RootState) => state.qasCopyAnswersWithCleaning.copyingAnswersStart;
export const selectCopyingAnswersStopStatus = (state: RootState) => state.qasCopyAnswersWithCleaning.copyingAnswersStop;
export const selectCopyingAnswersProcessStatus = (state: RootState) => state.qasCopyAnswersWithCleaning.copyingAnswersStatus;
export const selectCopyingAnswersLog = (state: RootState) => state.qasCopyAnswersWithCleaning.copyingAnswersLog;

export default qasCopyAnswersWithCleaningSlice.reducer;
